import { countOpenAlerts, countOpenSosAlerts } from "../data/cad-feed";
import type { AlertRow } from "../data/alert-queue";
import type { AsyncStatus } from "../data/useReadModel";
import { useRelativeTimeTicker } from "../hooks/useRelativeTimeTicker";
import { formatDisplayTime } from "../lib/display-time";

/**
 * Finer grain than LiveTimestamp for the desk stamp: "Just now" was wrong while poll/realtime
 * rewrote the epoch every ~30s, and anything under 45s always looked frozen and healthy.
 */
function formatDeskRefreshAge(iso: string, nowMs: number, timeZone: string): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) {
    return "Updated...";
  }
  const seconds = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (seconds < 5) {
    return "Updated Just now";
  }
  if (seconds < 60) {
    return `Updated ${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `Updated ${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `Updated ${hours}h ago`;
  }
  return `Updated ${formatDisplayTime(iso, timeZone)}`;
}

/**
 * Floating glass status chrome over the CAD desk: workspace, open alert pressure, live feed
 * status, last refresh. The page owns the alert read and shares it with the dock badges so the
 * strip and the SOS bubble can never disagree about what is open.
 */
export function CadStatusBar({
  accountName,
  timeZone,
  alerts,
  alertsStatus,
  lastRefreshedAt,
  realtimeConnected,
  onRefresh,
  refreshBusy = false,
}: {
  accountName: string;
  timeZone: string;
  alerts: AlertRow[] | null;
  alertsStatus: AsyncStatus;
  lastRefreshedAt: number | null;
  realtimeConnected: boolean;
  /** Manual desk refresh: reloads map layers, badges, and open tile feeds. */
  onRefresh?: (() => void) | undefined;
  refreshBusy?: boolean | undefined;
}) {
  useRelativeTimeTicker();
  const openCount = countOpenAlerts(alerts ?? []);
  const openSosCount = countOpenSosAlerts(alerts ?? []);
  const refreshedIso = lastRefreshedAt !== null ? new Date(lastRefreshedAt).toISOString() : null;
  const nowMs = Date.now();
  const updatedLabel =
    refreshedIso === null
      ? "Updated..."
      : formatDeskRefreshAge(refreshedIso, nowMs, timeZone);
  const updatedTitle =
    refreshedIso === null
      ? "Click to refresh the Command Center desk"
      : `Last desk data pull ${formatDisplayTime(refreshedIso, timeZone)}. Click to refresh map and feeds now.`;

  return (
    <header className="cad__strip" aria-label="Command center status">
      <div className="cad__strip-left">
        <span className="cad__strip-label">Command center</span>
        <span className="cad__strip-workspace">{accountName}</span>
      </div>
      <div className="cad__strip-metrics">
        {openSosCount > 0 ? (
          <span className="cad__chip cad__chip--sos" aria-live="assertive">
            {openSosCount} open SOS
          </span>
        ) : null}
        {/*
          Lock 5: proof green marks only DUTY: LIVE and verified check-ins, and pulse is
          reserved for true live duty and hot SOS. Zero open alerts and a healthy feed are
          resting states, so both chips stay neutral - no green, no breath.
        */}
        <span
          className={`cad__chip ${openCount > 0 ? "cad__chip--warn" : "cad__chip--muted"}`}
          aria-live="polite"
        >
          {alertsStatus === "loading" && alerts === null
            ? "Alerts..."
            : `${openCount} open alert${openCount === 1 ? "" : "s"}`}
        </span>
        <span className="cad__chip cad__chip--feed cad__chip--muted">
          {realtimeConnected ? (
            <>
              <span className="cad__feed-dot" aria-hidden="true" /> Live feed
            </>
          ) : (
            "Connecting feed..."
          )}
        </span>
        {onRefresh !== undefined ? (
          <button
            type="button"
            className={`cad__chip cad__chip--muted cad__chip--updated cad__chip--action${refreshBusy ? " cad__chip--busy" : ""}`}
            onClick={onRefresh}
            disabled={refreshBusy}
            title={updatedTitle}
            aria-label={refreshBusy ? "Refreshing desk data" : `${updatedLabel}. Refresh desk data`}
          >
            {refreshBusy ? "Refreshing..." : updatedLabel}
          </button>
        ) : (
          <span className="cad__chip cad__chip--muted cad__chip--updated" title={updatedTitle}>
            {updatedLabel}
          </span>
        )}
      </div>
    </header>
  );
}
